/**
 * Tiny viewport hook — reports what device / orientation / input
 * modality we're on. Drives the mobile-responsive layout (bottom
 * sheet vs side drawer, cascading pills, back-gesture interception,
 * hover-vs-tap affordance visibility).
 *
 * - `isMobile` is width-based (small phone screen OR narrow desktop
 *   window). Catches phones in portrait, doesn't catch phones in
 *   landscape (whose viewport width exceeds MOBILE_MAX_WIDTH).
 * - `isPortrait` distinguishes portrait (bottom sheet) from landscape
 *   (side drawer).
 * - `isTouch` reports whether the primary pointer is coarse (finger,
 *   stylus, TV remote — anything without hover). Independent of
 *   width, so it correctly flags landscape phones AND tablets AND
 *   iPad-in-Chrome-DevTools-mobile-emulation as touch-first, which
 *   `isMobile` alone misses. Prefer this for "should we always show
 *   affordances that were hover-revealed on desktop?"
 */
import { useEffect, useState } from 'react';

export type Viewport = {
  isMobile: boolean;
  isPortrait: boolean;
  isTouch: boolean;
};

const MOBILE_MAX_WIDTH = 768;

function read(): Viewport {
  if (typeof window === 'undefined') {
    return { isMobile: false, isPortrait: false, isTouch: false };
  }
  // `pointer: coarse` is the CSS Media Queries Level 4 selector for
  // "primary pointer is imprecise" (finger, TV remote, etc.). Every
  // modern browser supports it. Falls back to false if matchMedia
  // isn't available (very old browsers, non-DOM env).
  const isTouch =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
  return {
    isMobile: window.innerWidth <= MOBILE_MAX_WIDTH,
    isPortrait: window.innerHeight >= window.innerWidth,
    isTouch,
  };
}

export function useViewport(): Viewport {
  const [vp, setVp] = useState<Viewport>(read);
  useEffect(() => {
    const onChange = () => setVp(read());
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    // Also re-read when pointer capability changes (e.g. Surface
    // detaches keyboard, external mouse attaches to a tablet). The
    // matchMedia listener fires only on that transition.
    const mq =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(pointer: coarse)')
        : null;
    const onPointer = () => setVp(read());
    mq?.addEventListener('change', onPointer);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
      mq?.removeEventListener('change', onPointer);
    };
  }, []);
  return vp;
}
